// src/views/Reports/Reports.jsx
import { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import { getRecipes, getMaterials, getAlerts } from '../../api';
import InventoryTrendChart from './InventoryTrendChart';
import RecipeCostAnalysis from './RecipeCostAnalysis';
import StockLevelGauge from './StockLevelGauge';
import ExpiryHeatmap from './ExpiryHeatmap';
import IngredientUsageChart from './IngredientUsageChart';
import { FiPieChart, FiTrendingUp, FiAlertTriangle, FiDollarSign, FiBox } from 'react-icons/fi';

const Reports = () => {
  const [loading, setLoading] = useState(true);
  const [inventoryData, setInventoryData] = useState([]);
  const [recipeData, setRecipeData] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [timeRange, setTimeRange] = useState('30d');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [materials, recipes, alertData] = await Promise.all([
          getMaterials(),
          getRecipes(),
          getAlerts()
        ]);
        
        // Ensure recipes have properly formatted ingredients
        const normalizedRecipes = recipes.map(recipe => {
          let ingredients = [];
          try {
            ingredients = typeof recipe.ingredients === 'string' 
              ? JSON.parse(recipe.ingredients) 
              : (Array.isArray(recipe.ingredients) ? recipe.ingredients : []);
          } catch (e) {
            console.error('Error parsing ingredients:', e);
            ingredients = [];
          }

          return {
            ...recipe,
            ingredients: ingredients.filter(ing => ing && ing.name) // Filter out invalid ingredients
          };
        });

        setInventoryData(materials);
        setRecipeData(normalizedRecipes);
        setAlerts(alertData);
      } catch (error) {
        console.error('Error fetching report data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const calculateInventoryStats = () => {
    const totalItems = inventoryData.length;
    const totalValue = inventoryData.reduce((sum, item) => 
      sum + (item.price_per_unit * (item.current_quantity || 0)), 0);
    
    const lowStockItems = inventoryData.filter(item => 
      (item.current_quantity || 0) <= (item.min_quantity || 0)).length;
    
    const expiringSoon = inventoryData.filter(item => {
      if (!item.expiry_date) return false;
      try {
        const expiryDate = new Date(item.expiry_date);
        const today = new Date();
        const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
        return daysUntilExpiry > 0 && daysUntilExpiry <= (item.permissible_expiry_days || 7);
      } catch (e) {
        return false;
      }
    }).length;

    return { totalItems, totalValue, lowStockItems, expiringSoon };
  };

  const calculateRecipeStats = () => {
    const totalRecipes = recipeData.length;
    const avgCost = recipeData.reduce((sum, recipe) => sum + (recipe.cost || 0), 0) / (totalRecipes || 1);
    
    const ingredientsUsage = {};
    recipeData.forEach(recipe => {
      if (Array.isArray(recipe.ingredients)) {
        recipe.ingredients.forEach(ing => {
          if (ing && ing.name) {
            ingredientsUsage[ing.name] = (ingredientsUsage[ing.name] || 0) + 1;
          }
        });
      }
    });
    
    const mostUsedIngredients = Object.entries(ingredientsUsage)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);

    return { totalRecipes, avgCost, mostUsedIngredients };
  };

  const inventoryStats = calculateInventoryStats();
  const recipeStats = calculateRecipeStats();

  return (
    <Layout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Analytics Dashboard</h1>
        <p className="text-gray-500 mt-2">Visual insights into your inventory and recipes</p>
      </div>

      {/* Time range selector */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-2 overflow-x-auto pb-2">
          {['Overview', 'Inventory', 'Recipes', 'Alerts'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab.toLowerCase())}
              className={`px-4 py-2 rounded-md whitespace-nowrap ${
                activeTab === tab.toLowerCase()
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm"
        >
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
          <option value="all">All time</option>
        </select>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Inventory Items</p>
                  <h3 className="text-2xl font-bold mt-1">{inventoryStats.totalItems}</h3>
                </div>
                <div className="p-3 rounded-full bg-blue-100 text-blue-600">
                  <FiBox size={20} />
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Inventory Value</p>
                  <h3 className="text-2xl font-bold mt-1">
                    ₹{inventoryStats.totalValue.toLocaleString('en-IN')}
                  </h3>
                </div>
                <div className="p-3 rounded-full bg-green-100 text-green-600">
                  <FiDollarSign size={20} />
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Low Stock Items</p>
                  <h3 className="text-2xl font-bold mt-1">{inventoryStats.lowStockItems}</h3>
                </div>
                <div className="p-3 rounded-full bg-yellow-100 text-yellow-600">
                  <FiAlertTriangle size={20} />
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Recipes</p>
                  <h3 className="text-2xl font-bold mt-1">{recipeStats.totalRecipes}</h3>
                </div>
                <div className="p-3 rounded-full bg-purple-100 text-purple-600">
                  <FiPieChart size={20} />
                </div>
              </div>
            </div>
          </div>

          {/* Main Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <h3 className="font-medium text-lg mb-4 flex items-center gap-2">
                <FiTrendingUp /> Inventory Trend
              </h3>
              <InventoryTrendChart data={inventoryData} timeRange={timeRange} />
            </div>

            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <h3 className="font-medium text-lg mb-4 flex items-center gap-2">
                <FiDollarSign /> Recipe Cost Analysis
              </h3>
              <RecipeCostAnalysis data={recipeData} />
            </div>
          </div>

          {/* Secondary Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <h3 className="font-medium text-lg mb-4">Stock Levels</h3>
              <StockLevelGauge data={inventoryData} />
            </div>

            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <h3 className="font-medium text-lg mb-4">Expiry Heatmap</h3>
              <ExpiryHeatmap data={inventoryData} />
            </div>

            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <h3 className="font-medium text-lg mb-4">Top Ingredients</h3>
              <IngredientUsageChart data={recipeStats.mostUsedIngredients || []} />
            </div>
          </div>
        </div>
      )}

      {/* Inventory Tab */}
      {activeTab === 'inventory' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <h3 className="font-medium text-lg mb-4">Inventory Value by Category</h3>
              <InventoryTrendChart data={inventoryData} showByCategory={true} />
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <h3 className="font-medium text-lg mb-4">Stock Levels Overview</h3>
              <StockLevelGauge data={inventoryData} detailed={true} />
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <h3 className="font-medium text-lg mb-4">Expiry Timeline</h3>
            <ExpiryHeatmap data={inventoryData} detailed={true} />
          </div>
        </div>
      )}

      {/* Recipes Tab */}
      {activeTab === 'recipes' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <h3 className="font-medium text-lg mb-4">Recipe Cost Distribution</h3>
              <RecipeCostAnalysis data={recipeData} detailed={true} />
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <h3 className="font-medium text-lg mb-4">Preparation Time Analysis</h3>
              {/* Add preparation time chart component */}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <h3 className="font-medium text-lg mb-4">Ingredient Usage</h3>
            <IngredientUsageChart data={recipeStats.mostUsedIngredients || []} detailed={true} />
          </div>
        </div>
      )}

      {/* Alerts Tab */}
      {activeTab === 'alerts' && (
        <div className="space-y-4">
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <h3 className="font-medium text-lg mb-4">Current Alerts ({alerts.length})</h3>
            {alerts.length > 0 ? (
              <div className="space-y-3">
                {alerts.map(alert => (
                  <div key={alert.id} className="p-3 bg-yellow-50 rounded-md border-l-4 border-yellow-500">
                    <div className="flex justify-between">
                      <div>
                        <p className="font-medium">{alert.name}</p>
                        <p className="text-sm text-gray-600">
                          {alert.category} • {alert.current_quantity} {alert.unit}
                        </p>
                      </div>
                      <div className="text-right">
                        {alert.stock_message && (
                          <p className="text-sm font-medium text-yellow-800">
                            {alert.stock_message}
                          </p>
                        )}
                        {alert.expiry_message && (
                          <p className="text-sm text-yellow-700">
                            {alert.expiry_message}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No active alerts</p>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Reports;